import React from "react";
import PropTypes from "prop-types";
import moment from "moment";
import { Modal, ModalSize } from "components/notifications";
import { ShowHide } from "components/layout";

const defaultFunction = () => { };

// The account reporting start date and the DVSA scheme start date are shown in full, e.g. "14 July 2025".
const startDateFormat = "DD MMMM YYYY";

const alertTriggerRules = [
    "one red breach in the period being assessed",
    "two or more ambers across the last three periods",
    "three or more breaches across the last three periods",
    "five or more breaches across the last thirteen periods"
];

// M5 is a maintenance KPI, and what the DVSA is told when an alert is raised differs by stream, so
// both are worded per stream rather than shared.
const streamCopy = {
    Drivers: {
        m5Note: null,
        activeLine: "Alerts are emailed to the DVSA, with repeat offenders and MSIs. Unaccounted km are never reported."
    },
    Maintenance: {
        m5Note: "M5 never raises an alert on its own. When M1 to M4 raise one, the M5 figure is included.",
        activeLine: "Alerts are emailed to the DVSA, with the M5 figure."
    }
};

// The counting window restarts at the period after the one that alerted, so periods up to and
// including it stop counting towards the next assessment.
const resetLine = "After an alert is raised, counting starts again from the following period.";

const pendingLine = "These are the DVSA scheme rules. This account has not started submitting yet, so nothing has been sent.";
const notSubmittingLine = "These are the DVSA scheme rules. This account is not currently submitting, so nothing is sent to the DVSA.";

// The trigger rules hold for every account, so an account with no submission block — or one in a state
// this modal has no line for — still gets the rules, just without the closing sentence about the DVSA.
function resolveClosingLine(activeLine, dvsaSubmission) {
    if (dvsaSubmission == null) {
        return null;
    }

    if (dvsaSubmission.state === "Active") {
        return activeLine;
    }

    if (dvsaSubmission.state === "Pending") {
        return pendingLine;
    }

    if (dvsaSubmission.state === "NotSubmitting") {
        return notSubmittingLine;
    }

    return null;
}

// What raises an alert on the Earned Recognition KPI grids: the four scheme trigger rules, the
// maintenance-only M5 provision, and where the account stands with the DVSA. showStartDateLine and
// showPreSchemeCaveat come from the grid's own window, so each caveat only shows while a period
// predating its date is on screen. Those two dates are independent, so both lines can appear at once.
export function ErAlertRulesModal(
    {
        isOpen = false,
        onClose = defaultFunction,
        stream = "Drivers",
        dvsaSubmission = null,
        showStartDateLine = false,
        kpiStartFloor = null,
        schemeStartDate = null,
        showPreSchemeCaveat = false
    }
) {
    const copy = streamCopy[stream];
    const closingLine = resolveClosingLine(copy.activeLine, dvsaSubmission);

    let startDateLine = null;
    if (showStartDateLine && kpiStartFloor != null && moment(kpiStartFloor).isValid()) {
        startDateLine = `Your account started reporting on ${moment(kpiStartFloor).format(startDateFormat)}. Periods before that are not counted towards your assessment.`;
    }

    // An account with no DVSA number or no recorded start date says nothing about the scheme at all,
    // so an absent date renders no line rather than a placeholder.
    let schemeStartLine = null;
    const schemeStart = schemeStartDate == null ? null : moment(schemeStartDate);
    if (schemeStart != null && schemeStart.isValid()) {
        // An account can be Active while its scheme start is still days away, because the states are
        // derived from period ordinals two periods behind, so the tense follows the date on screen.
        let schemeStartOpening = "You started reporting";
        if (schemeStart.isAfter(moment(), "day")) {
            schemeStartOpening = "You will start reporting";
        }

        schemeStartLine = `${schemeStartOpening} to the DVSA on ${schemeStart.format(startDateFormat)}.`;
        if (showPreSchemeCaveat) {
            schemeStartLine = `${schemeStartLine} Periods before that do not count towards an alert.`;
        }
    }

    return (
        <Modal
            isOpen={isOpen}
            onClose={onClose}
            title="What raises an alert"
            size={ModalSize.MEDIUM}
            clickOutsideClose={true}
        >
            <div>
                <p>An alert is raised when any of these happens:</p>
                <ul>
                    {alertTriggerRules.map((rule) => (<li key={rule}>{rule}</li>))}
                </ul>
                <ShowHide
                    evaluator={copy.m5Note != null}
                    show={(<p>{copy.m5Note}</p>)}
                />
                <p>{resetLine}</p>
                <ShowHide
                    evaluator={startDateLine != null}
                    show={(<p>{startDateLine}</p>)}
                />
                <ShowHide
                    evaluator={schemeStartLine != null}
                    show={(<p>{schemeStartLine}</p>)}
                />
                <ShowHide
                    evaluator={closingLine != null}
                    show={(<p>{closingLine}</p>)}
                />
            </div>
        </Modal>
    );
}

ErAlertRulesModal.propTypes = {
    isOpen: PropTypes.bool,
    onClose: PropTypes.func,
    stream: PropTypes.oneOf(["Drivers", "Maintenance"]),
    dvsaSubmission: PropTypes.object,
    showStartDateLine: PropTypes.bool,
    kpiStartFloor: PropTypes.string,
    schemeStartDate: PropTypes.string,
    showPreSchemeCaveat: PropTypes.bool
};
